import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, User, MapPin, Package, CheckCircle, ChevronRight, Truck, AlertTriangle, Boxes, XCircle, Clock, Users, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const MobileSFGPicking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const slipNo = searchParams.get('slip');
  const scanInputRef = useRef(null);

  // States
  const [step, setStep] = useState('staff'); // staff, select-slip, picking, combo-picking, handover, combo-v2-handover, success
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [pickSlips, setPickSlips] = useState([]);
  const [currentSlip, setCurrentSlip] = useState(null);
  const [currentBundle, setCurrentBundle] = useState(null);
  const [bundleDesignName, setBundleDesignName] = useState('');
  const [bundleShade, setBundleShade] = useState('');
  const [bundleSize, setBundleSize] = useState('');
  const [bundleDescription, setBundleDescription] = useState('');
  const [scannedCode, setScannedCode] = useState('');
  const [progress, setProgress] = useState({ picked: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [handoverInfo, setHandoverInfo] = useState(null);  // Legacy single staff info
  
  // Multi-staff handover states
  const [handoverAssignments, setHandoverAssignments] = useState([]);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [skipRemarks, setSkipRemarks] = useState('');
  const [handoverId, setHandoverId] = useState(null);
  const [handoverSlipNo, setHandoverSlipNo] = useState(null);  // Track which slip the handover belongs to
  const [isQueued, setIsQueued] = useState(false);
  const [confirmingAssignmentId, setConfirmingAssignmentId] = useState(null);
  const [hasOtherPendingHandovers, setHasOtherPendingHandovers] = useState(false);  // Warn about other pending handovers
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [capacityInfo, setCapacityInfo] = useState(null);
  const [unassignedBundles, setUnassignedBundles] = useState([]);  // Track bundles that couldn't be assigned
  // Combo V2 distributor handover (V2 slips skip the regular PP staff handover
  // and route to the Piece Distributor instead).
  const [comboV2Handover, setComboV2Handover] = useState(null);
  // SFG picker must explicitly confirm physical handover before moving on.
  // This guards against accidental "next slip" taps before the bundles
  // actually leave their hands.
  const [comboV2HandoverConfirmed, setComboV2HandoverConfirmed] = useState(false);
  const [comboV2HandoverSubmitting, setComboV2HandoverSubmitting] = useState(false);
  // null when not a V2 slip; otherwise:
  // { slip_no, bulk_plan_id, total_bundles, total_pieces, bundles, handover_route }
  
  // Combo picking states
  const [isCombo, setIsCombo] = useState(false);
  const [comboGroup, setComboGroup] = useState(null);
  const [currentComponent, setCurrentComponent] = useState(null);
  const [componentProgress, setComponentProgress] = useState({});
  const [showUnavailableDialog, setShowUnavailableDialog] = useState(false);
  const [showHandoverDialog, setShowHandoverDialog] = useState(false);
  const [expandedAlreadyPicked, setExpandedAlreadyPicked] = useState({}); // { [component_barcode]: true }
  
  // Pending handover queue (unassigned bundles from previous handovers)
  const [pendingQueueBundles, setPendingQueueBundles] = useState([]);
  const [pendingQueueTotal, setPendingQueueTotal] = useState({ bundles: 0, pieces: 0 });

  // ===== Place-on-floor flow + picker notifications =====
  // queueId of the handover-queue entry currently shown on the "queued"
  // screen — required by the floor-placement confirmation API.
  const [queuedHandoverQueueId, setQueuedHandoverQueueId] = useState(null);
  const [floorPlacementConfirmed, setFloorPlacementConfirmed] = useState(false);
  const [floorPlacementSubmitting, setFloorPlacementSubmitting] = useState(false);
  // Floor-ready handovers — bundles the picker placed on floor that
  // have now been auto-assigned. Surfaced as a sticky banner.
  const [floorReadyHandovers, setFloorReadyHandovers] = useState([]);
  // Pending unconfirmed handovers — used to resume the handover screen
  // when a picker reloads the page mid-handover.
  const [pendingUnconfirmedHandovers, setPendingUnconfirmedHandovers] = useState([]);

  useEffect(() => {
    if (slipNo && step === 'staff') {
      // Direct link to a slip - handled in staff validate
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slipNo]);

  useEffect(() => {
    if (step === 'staff') {
      setTimeout(() => document.getElementById('staff-input')?.focus(), 100);
    } else if (step === 'picking') {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  }, [step]);

  const vibrate = (pattern = [50]) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  // Fetch unassigned bundles from handover queue
  const fetchPendingHandoverQueue = async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/handover-queue?status=queued');
      if (response.data.success) {
        setPendingQueueBundles(response.data.items || []);
        setPendingQueueTotal({
          bundles: response.data.total_bundles || 0,
          pieces: response.data.total_pieces || 0
        });
      }
    } catch (error) {
      console.error('Error fetching handover queue:', error);
    }
  };

  // Fetch handover queue on mount and when step changes
  useEffect(() => {
    fetchPendingHandoverQueue();
  }, [step]);

  // Poll picker notifications every 30s once the picker is logged in,
  // so floor-ready and pending-unconfirmed alerts surface without a
  // refresh. Skipped on the staff-entry screen (no picker yet).
  useEffect(() => {
    if (!staffId || step === 'staff') return;
    const tick = () => fetchPickerNotifications(staffId);
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffId, step]);

  // Track bundles we've already auto-skipped (to avoid re-skipping on every poll)
  const autoSkippedCodesRef = useRef(new Set());

  // LIVE polling for combo picking: every 5s, refresh the combo group and silently
  // auto-skip any bundle that has been picked by another picker in the meantime.
  useEffect(() => {
    if (step !== 'combo-picking' || !comboGroup?.slip_group_id) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const response = await apiClient.get(`/wms/combo-pick/group/${comboGroup.slip_group_id}`);
        if (cancelled) return;
        // RACE GUARD (2026-04-24 fix): if the user just completed picking and the handover
        // dialog is open, don't let a late-arriving poll reassign currentComponent back to a
        // just-completed component. That was causing "UI stuck" after skipping the last bundle.
        if (showHandoverDialog) return;
        const freshGroup = response.data?.group;
        if (!freshGroup) return;

        // Detect any bundle that flipped to 'with_pp_staff' since we last rendered
        const skippedThisRound = [];
        for (const comp of (freshGroup.components || [])) {
          for (const b of (comp.bundles_to_pick || [])) {
            if (b.skipped || b.picked) continue;
            if (b.location_status === 'with_pp_staff' && !autoSkippedCodesRef.current.has(b.bundle_code)) {
              autoSkippedCodesRef.current.add(b.bundle_code);
              try {
                await apiClient.post(
                  `/wms/combo-pick/skip-bundle?slip_group_id=${comboGroup.slip_group_id}&component_barcode=${comp.component_barcode}&bundle_code=${b.bundle_code}`
                );
                const pickerName = b.picked_by_staff_name || b.with_pp_staff_name || 'another picker';
                skippedThisRound.push(`${b.bundle_code} — Already picked by ${pickerName}`);
              } catch (err) {
                // silent fail — bundle will be retried on next poll
                autoSkippedCodesRef.current.delete(b.bundle_code);
              }
            }
          }
        }

        // Re-fetch the enriched group after any auto-skips so UI reflects new state
        if (skippedThisRound.length > 0) {
          const refreshed = await apiClient.get(`/wms/combo-pick/group/${comboGroup.slip_group_id}`);
          if (cancelled) return;
          const updatedGroup = refreshed.data?.group;
          if (updatedGroup) {
            setComboGroup(updatedGroup);
            // Sync componentProgress with server-side picked_bundles — otherwise the
            // top-right "0/2" counter stays stale after auto-skips and can contradict
            // the actual state (green check but 0/2 shown).
            setComponentProgress((prev) => {
              const next = { ...prev };
              (updatedGroup.components || []).forEach((c) => {
                next[c.component_barcode] = {
                  picked: c.picked_bundles || 0,
                  total: c.required_bundles || c.required_qty || 0,
                };
              });
              return next;
            });
            // Recompute current component: skip components fully complete / all-already-picked
            const nextIncomplete = (updatedGroup.components || []).find(c => {
              const req = c.required_bundles || c.required_qty || 1;
              return (c.picked_bundles || 0) < req && c.status !== 'COMPLETED' && !c.all_bundles_already_picked;
            });
            if (nextIncomplete && nextIncomplete.component_barcode !== currentComponent?.component_barcode) {
              setCurrentComponent(nextIncomplete);
            } else if (!nextIncomplete) {
              setCurrentComponent(null);
              setShowHandoverDialog(true);
            } else {
              setCurrentComponent(nextIncomplete);
            }
          }
          // Log line per skipped bundle (silent toast, no vibration)
          skippedThisRound.forEach(msg => toast.info(msg, { duration: 4000 }));
        } else {
          // Still update comboGroup so live bin_location / status changes reflect
          setComboGroup(freshGroup);
          // Sync componentProgress to server truth in case any state drifted
          setComponentProgress((prev) => {
            const next = { ...prev };
            (freshGroup.components || []).forEach((c) => {
              next[c.component_barcode] = {
                picked: c.picked_bundles || 0,
                total: c.required_bundles || c.required_qty || 0,
              };
            });
            return next;
          });
        }
      } catch (err) {
        // silent — keep existing state
      }
    };

    const interval = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, comboGroup?.slip_group_id, currentComponent?.component_barcode, showHandoverDialog]);
  
  const [showQueueOverlay, setShowQueueOverlay] = useState(false);

  const handleStaffValidate = async () => {
    if (!staffId.trim()) {
      toast.error('Please scan Staff ID');
      vibrate([100, 50, 100]);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${staffId}`);
      if (response.data.valid) {
        setStaffName(response.data.staff_name);
        vibrate([50]);
        toast.success(`Welcome, ${response.data.staff_name}!`);

        // RESUME GUARD: if this picker has a pending un-confirmed
        // handover from a previous session (e.g., they refreshed the
        // page mid-handover or the network dropped), force them back
        // onto the handover screen for that slip BEFORE handing them
        // a new slip. Bundles must not be silently abandoned on the
        // floor with no record.
        const notifs = await fetchPickerNotifications(staffId);
        if (notifs && (notifs.pending_unconfirmed_count || 0) > 0) {
          const mostRecent = notifs.pending_unconfirmed[0];
          resumeHandoverFromNotification(mostRecent);
          return;
        }

        if (slipNo) {
          // Direct link to a specific slip
          await startSlip(slipNo);
        } else {
          // Auto-assign next slip from queue
          await getNextAssignedSlip();
        }
      } else {
        toast.error('Invalid Staff ID');
        vibrate([100, 50, 100]);
      }
    } catch (error) {
      toast.error('Staff validation failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const getNextAssignedSlip = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/wms/pick-slips/next-assigned?staff_id=${staffId}`);
      
      if (response.data.has_slip) {
        if (response.data.is_combo) {
          // Combo pick slip group
          setIsCombo(true);
          setComboGroup(response.data.slip);
          setCurrentSlip(response.data.slip);
          
          // Initialize component progress
          const compProgress = {};
          response.data.slip.components?.forEach(comp => {
            compProgress[comp.component_barcode] = {
              picked: comp.picked_bundles || 0,
              total: comp.required_bundles || 0
            };
          });
          setComponentProgress(compProgress);
          
          // Set first incomplete component as current (skip already-picked ones)
          const firstIncomplete = response.data.slip.components?.find(
            c => {
              const reqBundles = c.required_bundles || c.required_qty || 1;
              return (c.picked_bundles || 0) < reqBundles && c.status !== 'COMPLETED' && !c.all_bundles_already_picked;
            }
          );
          setCurrentComponent(firstIncomplete || null);
          
          // Check if resuming an in-progress group
          const anyPicked = response.data.slip.components?.some(c => (c.picked_bundles || 0) > 0);
          if (anyPicked && firstIncomplete) {
            toast.success(`Resuming combo pick: ${response.data.slip.combo_name} — ${firstIncomplete.component_name}`);
          } else if (!firstIncomplete) {
            toast.success(`All components picked for ${response.data.slip.combo_name} — complete handover`);
          } else {
            toast.success(`Combo picking task: ${response.data.slip.combo_name}`);
          }
          setStep('combo-picking');
        } else {
          // Regular pick slip
          setIsCombo(false);
          setComboGroup(null);
          setCurrentSlip(response.data.slip);
          toast.success(response.data.message);
          // Get first bundle — only enter the picking screen if there's a
          // bundle to pick. A completed slip routes to 'success' inside
          // fetchNextBundle; forcing 'picking' here showed "No Bundle Loaded".
          const hasNext = await fetchNextBundle(response.data.slip.slip_no);
          if (hasNext) setStep('picking');
        }
      } else {
        // No slips in queue
        setPickSlips([]);
        setIsCombo(false);
        setStep('select-slip'); // Show empty state
        toast.info(response.data.message || 'No pick slips available');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get next slip');
      setStep('select-slip');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingSlips = async () => {
    // This is now only used for showing empty state count
    try {
      const response = await apiClient.get('/wms/pick-slips?status=PENDING');
      setPickSlips(response.data.pick_slips || []);
    } catch (error) {
      toast.error('Failed to fetch pick slips');
    }
  };

  const startSlip = async (slipNumber) => {
    try {
      setLoading(true);
      // Start the slip
      await apiClient.post(`/wms/pick-slips/${slipNumber}/start?staff_id=${staffId}`);
      
      // Get slip details
      const detailResponse = await apiClient.get(`/wms/pick-slips/${slipNumber}`);
      setCurrentSlip(detailResponse.data);
      
      // Get first bundle — only enter the picking screen if a bundle loaded.
      // A completed slip routes to 'success' inside fetchNextBundle; forcing
      // 'picking' here is what produced the bogus "No Bundle Loaded" screen.
      const hasNext = await fetchNextBundle(slipNumber);
      if (hasNext) setStep('picking');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start slip');
    } finally {
      setLoading(false);
    }
  };

  const fetchNextBundle = async (slipNumber) => {
    try {
      const response = await apiClient.get(`/wms/pick-slips/${slipNumber}/next-bundle`);
      
      if (response.data.has_next) {
        setCurrentBundle(response.data.bundle);
        setBundleDesignName(response.data.design_name || response.data.sku_code || '');
        setBundleShade(response.data.shade || '');
        setBundleSize(response.data.size || '');
        setBundleDescription(response.data.description || '');
        setProgress(response.data.progress);
        setTimeout(() => scanInputRef.current?.focus(), 100);
        return true;
      } else {
        // Slip is already fully picked/skipped — go straight to the success
        // screen. Returns false so the caller does NOT override the step back
        // to 'picking' (that override was what produced the bogus
        // "No Bundle Loaded" screen when (re)opening a completed slip).
        setCurrentBundle(null);
        const prog = response.data.progress;
        setProgress(prog || { picked: 0, total: 0, percentage: 100 });
        setStep('success');
        return false;
      }
    } catch (error) {
      console.error('fetchNextBundle error:', error);
      toast.error('Failed to get next bundle — use recovery options below');
      setCurrentBundle(null);
      // Real fetch failure — surface the recovery screen (Retry / Skip).
      setStep('picking');
      return false;
    }
  };

  const handleBundlePick = async () => {
    if (!scannedCode.trim()) {
      toast.error('Please scan bundle');
      vibrate([100, 50, 100]);
      return;
    }

    if (scannedCode !== currentBundle?.bundle_code) {
      toast.error('Wrong bundle! Scan the correct one.');
      vibrate([200, 100, 200]);
      setScannedCode('');
      scanInputRef.current?.focus();
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post(
        `/wms/pick-slips/${currentSlip.slip_no}/pick-bundle?bundle_code=${scannedCode}`
      );

      // RACE: the bundle was grabbed by another picker/flow between the moment
      // it was shown and now. The backend auto-skipped this line (without
      // corrupting inventory) and may have attached a fresh alternate. Advance
      // gracefully instead of dead-ending the picker on the consumed bundle.
      if (response.data.auto_skipped) {
        vibrate([200, 100, 200]);
        setScannedCode('');
        const alt = response.data.alternate_bundle;
        if (alt) {
          toast(
            <div className="flex flex-col gap-1">
              <p className="font-semibold text-amber-700">Bundle already taken — use this alternate</p>
              <p className="text-sm"><strong>{alt.bundle_code}</strong></p>
              <p className="text-sm">Location: <strong>{alt.bin_location || 'N/A'}</strong></p>
              <p className="text-xs text-gray-500">{alt.design_name} · {alt.usn_count} pcs</p>
            </div>,
            { duration: 15000 }
          );
        } else {
          toast.warning(response.data.message || 'Bundle was just taken by another picker — moving on');
        }
        await fetchNextBundle(currentSlip.slip_no);
        setTimeout(() => scanInputRef.current?.focus(), 100);
        return;
      }

      vibrate([50, 50, 50]);
      toast.success('Bundle picked!');
      setScannedCode('');
      
      if (response.data.slip_completed) {
        // Trigger multi-staff auto-assignment
        await triggerMultiStaffHandover();
      } else {
        await fetchNextBundle(currentSlip.slip_no);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick bundle');
      vibrate([100, 50, 100]);
      setScannedCode('');
      scanInputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Skip bundle (not found / damaged) — remarks are MANDATORY (2026-06-12)
  const handleSkipBundle = async () => {
    if (!currentBundle || !currentSlip) return;
    if (!skipRemarks.trim()) {
      toast.error('Please enter remarks — why was the bundle not found?');
      return;
    }
    
    try {
      setLoading(true);
      setShowSkipConfirm(false);
      const response = await apiClient.post(
        `/wms/pick-slips/${currentSlip.slip_no}/skip-bundle?bundle_code=${currentBundle.bundle_code}&reason=not_found&notes=${encodeURIComponent(skipRemarks.trim())}`
      );
      setSkipRemarks('');
      
      vibrate([100, 50, 100]);
      toast.warning(response.data.message);

      // Show alternate bundle suggestion + lift it into the UI as the next
      // pickable bundle so the operator can immediately scan it without a
      // "wrong bundle" rejection.
      if (response.data.alternate_bundle) {
        const alt = response.data.alternate_bundle;
        toast(
          <div className="flex flex-col gap-1">
            <p className="font-semibold text-blue-700">Alternate Bundle Available</p>
            <p className="text-sm"><strong>{alt.bundle_code}</strong></p>
            <p className="text-sm">Location: <strong>{alt.bin_location || 'N/A'}</strong></p>
            <p className="text-xs text-gray-500">{alt.design_name} - {alt.usn_count} pcs</p>
          </div>,
          { duration: 15000 }
        );
        // Immediately set the alternate as the current pickable bundle so
        // scanning it succeeds. Mark `is_alternate` so the UI can highlight
        // and the user knows it replaces the not-found one.
        if (!response.data.slip_completed) {
          setCurrentBundle({
            bundle_code: alt.bundle_code,
            bin_location: alt.bin_location || '',
            design_name: alt.design_name || '',
            usn_count: alt.usn_count || 0,
            sku_code: alt.sku_code || '',
            is_alternate: true,
            replaces_bundle: currentBundle.bundle_code,
          });
          setScannedCode('');
          scanInputRef.current?.focus();
          return;
        }
      }

      setScannedCode('');

      if (response.data.slip_completed) {
        if (response.data.stats?.picked > 0) {
          await triggerMultiStaffHandover();
        } else {
          setStep('success');
        }
      } else {
        await fetchNextBundle(currentSlip.slip_no);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip bundle');
    } finally {
      setLoading(false);
    }
  };

  // Trigger multi-staff handover assignment
  const triggerMultiStaffHandover = async () => {
    try {
      setLoading(true);
      const currentSlipNo = currentSlip.slip_no;
      const response = await apiClient.post(
        `/wms/dispatch/pieces-processing/auto-assign?slip_no=${currentSlipNo}&picker_staff_id=${staffId}`
      );
      
      // Store the slip_no this handover belongs to - prefer from response for consistency
      const responseSlipNo = response.data.slip_no || currentSlipNo;
      setHandoverSlipNo(responseSlipNo);

      // ===== Combo V2 short-circuit =====
      // V2 slips don't get assigned to PP staff at picker handover — bundles
      // are floor-staged for the Piece Distributor to allocate later.
      if (response.data.is_combo_v2) {
        setComboV2Handover({
          slip_no: responseSlipNo,
          bulk_plan_id: response.data.bulk_plan_id,
          plan_name: response.data.plan_name,
          total_bundles: response.data.total_bundles || 0,
          total_pieces: response.data.total_pieces || 0,
          bundles: response.data.bundles || [],
          handover_route: response.data.handover_route || '/wms/combo-v2/distributor',
        });
        setComboV2HandoverConfirmed(false);  // require explicit confirm tick
        setIsQueued(false);
        setHandoverAssignments([]);
        setUnassignedBundles([]);
        toast.success(response.data.message || 'Bundles staged for Piece Distributor');
        setStep('combo-v2-handover');
        setLoading(false);
        return;
      }

      // Track unassigned bundles if any
      if (response.data.unassigned_bundles_list && response.data.unassigned_bundles_list.length > 0) {
        setUnassignedBundles(response.data.unassigned_bundles_list);
        toast.warning(`${response.data.unassigned_bundles} bundle(s) could not be assigned - queued for later`);
      } else {
        setUnassignedBundles([]);
      }
      
      if (response.data.queued) {
        // No staff capacity available - bundles are queued
        setIsQueued(true);
        setHandoverId(response.data.handover_id);
        setQueuedHandoverQueueId(response.data.handover_id);
        setFloorPlacementConfirmed(false);
        setHandoverAssignments([]);
        toast.info('All staff at capacity. Place bundles on the floor and confirm placement.');
        setStep('handover');
      } else if (response.data.assignments && response.data.assignments.length > 0) {
        // Bundles split across staff
        setIsQueued(false);
        setHandoverId(response.data.handover_id);
        setHandoverAssignments(response.data.assignments);
        const msg = response.data.partial 
          ? `Hand over ${response.data.assigned_bundles} bundle(s) to ${response.data.staff_count} staff. ${response.data.unassigned_bundles} bundle(s) queued.`
          : `Hand over to ${response.data.staff_count} staff member(s)`;
        toast.success(msg);
        setStep('handover');
      } else if ((response.data.total_bundles || 0) === 0) {
        // Truly nothing to hand over (e.g., all bundles skipped) — only
        // legitimate path that bypasses the handover screen.
        setStep('success');
      } else {
        // Defensive: non-zero bundles but no assignments and not queued.
        // This used to silently advance to "success" and let the next
        // slip overwrite the still-physically-held bundles. Block here
        // and surface a retry.
        console.error('Auto-assign returned empty assignments for bundles', response.data);
        setIsQueued(true);
        setQueuedHandoverQueueId(response.data.handover_id || null);
        setHandoverId(response.data.handover_id || null);
        setFloorPlacementConfirmed(false);
        setHandoverAssignments([]);
        toast.error(
          'Auto-assign returned no staff. Place bundles on the floor and tap "Retry" so a supervisor can review.',
        );
        setStep('handover');
      }
      
      // Check if there are other pending handovers for this picker
      checkOtherPendingHandovers(responseSlipNo);
    } catch (error) {
      console.error('Auto-assign error:', error);
      // No silent fall-through to "success" — that lets pickers walk
      // away with bundles still in hand. Stay on screen and offer retry.
      setIsQueued(true);
      setHandoverAssignments([]);
      setQueuedHandoverQueueId(null);
      setFloorPlacementConfirmed(false);
      setStep('handover');
      toast.error(
        error?.response?.data?.detail
          || 'Could not auto-assign staff. Place bundles on the floor and tap Retry.',
      );
    } finally {
      setLoading(false);
    }
  };
  
  // Check if picker has other pending handovers from different slips
  const checkOtherPendingHandovers = async (currentSlipNo) => {
    try {
      const response = await apiClient.get(
        `/wms/dispatch/pieces-processing/picker-handovers/${staffId}`
      );
      
      const otherHandovers = (response.data.pending_handovers || []).filter(
        h => h.slip_no !== currentSlipNo
      );
      
      if (otherHandovers.length > 0) {
        setHasOtherPendingHandovers(true);
        console.warn(`Picker has ${otherHandovers.length} other pending handover(s) from different slips`);
      } else {
        setHasOtherPendingHandovers(false);
      }
    } catch (error) {
      console.error('Error checking other handovers:', error);
    }
  };

  // Fetch picker notifications: floor-ready handovers + pending
  // unconfirmed handovers that must be resumed on refresh.
  const fetchPickerNotifications = async (sid) => {
    const id = sid || staffId;
    if (!id) return null;
    try {
      const response = await apiClient.get(
        `/wms/dispatch/picker-notifications/${encodeURIComponent(id)}`
      );
      setFloorReadyHandovers(response.data.floor_ready || []);
      setPendingUnconfirmedHandovers(response.data.pending_unconfirmed || []);
      return response.data;
    } catch (error) {
      console.error('Error fetching picker notifications:', error);
      return null;
    }
  };

  // Hydrate the handover screen from a "pending_unconfirmed" notification —
  // used after refresh so the picker is forced to confirm the handover
  // they left mid-flight before any new slip is fetched.
  const resumeHandoverFromNotification = (notif) => {
    if (!notif) return;
    setHandoverId(notif.handover_id);
    setHandoverSlipNo(notif.slip_no);
    setHandoverAssignments(
      (notif.assignments || []).map((a) => ({
        assignment_id: a.assignment_id,
        staff_id: a.staff_id,
        staff_name: a.staff_name,
        // 2026-05-20 — prefer the real bundles list (strings or {bundle_code}
        // dicts, depending on the storage path) so the "Bundles (N)" pill
        // shows the actual bundle codes. Fall back to empty placeholders
        // ONLY if the API didn't ship a bundles array yet — that path now
        // also yields a sized list so the count still renders.
        bundles: Array.isArray(a.bundles) && a.bundles.length
          ? a.bundles
          : Array.from({ length: a.bundle_count || 0 }, () => ({})),
        total_pieces: a.total_pieces,
        status: 'pending_handover',
      }))
    );
    setIsQueued(false);
    setQueuedHandoverQueueId(null);
    setFloorPlacementConfirmed(false);
    setStep('handover');
    setCurrentSlip({ slip_no: notif.slip_no });
    toast.warning(
      `Resuming pending handover for slip #${notif.slip_no} — please complete it before picking a new slip.`,
    );
  };

  // Picker confirms physical placement on the floor when no PP staff is free.
  const handleConfirmFloorPlacement = async () => {
    if (!queuedHandoverQueueId) {
      toast.error('No queue ID available to confirm placement');
      return;
    }
    try {
      setFloorPlacementSubmitting(true);
      const response = await apiClient.post(
        `/wms/dispatch/handover-queue/${encodeURIComponent(queuedHandoverQueueId)}/confirm-floor-placement?picker_staff_id=${encodeURIComponent(staffId)}`
      );
      vibrate([60, 60]);
      setFloorPlacementConfirmed(true);
      toast.success(response.data.message || 'Placement confirmed.');
      // Refresh notifications so the floor-queued counter updates.
      fetchPickerNotifications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm placement');
    } finally {
      setFloorPlacementSubmitting(false);
    }
  };

  // Dismiss a floor-ready notification (after picker walks the bundles
  // over and physically hands them to the now-assigned PP staff).
  const dismissFloorReadyNotification = async (handoverIdToDismiss) => {
    try {
      await apiClient.post(
        `/wms/dispatch/picker-notifications/${encodeURIComponent(handoverIdToDismiss)}/dismiss?picker_staff_id=${encodeURIComponent(staffId)}`
      );
      setFloorReadyHandovers((prev) =>
        prev.filter((n) => n.handover_id !== handoverIdToDismiss),
      );
    } catch (error) {
      console.error('Failed to dismiss notification', error);
    }
  };

  // Clear completed handovers that are stuck as pending
  const clearCompletedHandovers = async () => {
    try {
      setLoading(true);
      // Use force=true to clear handovers even if DB status isn't perfectly synced
      const response = await apiClient.post(
        `/wms/dispatch/pieces-processing/clear-completed-handovers/${staffId}?force=true`
      );
      
      if (response.data.cleared_count > 0) {
        toast.success(`Cleared ${response.data.cleared_count} handover(s): ${response.data.cleared_slips.join(', ')}`);
        setHasOtherPendingHandovers(false);
        
        // Re-check for any remaining pending handovers
        const currentSlipNo = handoverSlipNo || currentSlip?.slip_no;
        if (currentSlipNo) {
          checkOtherPendingHandovers(currentSlipNo);
        }
      } else {
        toast.info(response.data.message || 'No handovers to clear');
      }
    } catch (error) {
      console.error('Error clearing handovers:', error);
      toast.error('Failed to clear handovers');
    } finally {
      setLoading(false);
    }
  };

  // Fetch capacity diagnostic info
  const fetchCapacityInfo = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/wms/dispatch/pieces-processing/capacity-diagnostic');
      setCapacityInfo(response.data);
      setShowCapacityModal(true);
    } catch (error) {
      console.error('Error fetching capacity:', error);
      toast.error('Failed to load capacity info');
    } finally {
      setLoading(false);
    }
  };

  // Clear stuck capacity
  const clearStuckCapacity = async (staffId = null) => {
    try {
      setLoading(true);
      const url = staffId 
        ? `/wms/dispatch/pieces-processing/clear-stuck-capacity?staff_id=${staffId}&hours_threshold=1`
        : '/wms/dispatch/pieces-processing/clear-stuck-capacity?hours_threshold=1';
      
      const response = await apiClient.post(url);
      
      if (response.data.cleared_count > 0) {
        toast.success(response.data.message);
        // Refresh capacity info
        fetchCapacityInfo();
        // Try to process queue
        await apiClient.post('/wms/dispatch/pieces-processing/process-queue');
        refreshQueueStatus();
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error('Error clearing capacity:', error);
      toast.error('Failed to clear stuck capacity');
    } finally {
      setLoading(false);
    }
  };

  // Confirm handover to a specific staff member
  const confirmHandoverToStaff = async (assignmentId) => {
    try {
      setConfirmingAssignmentId(assignmentId);
      const response = await apiClient.post(
        `/wms/dispatch/pieces-processing/confirm-handover?handover_id=${handoverId}&assignment_id=${assignmentId}`
      );
      
      vibrate([50, 50]);
      toast.success(`Handed over to ${response.data.staff_name}`);
      
      // Update the assignment status locally
      setHandoverAssignments(prev => 
        prev.map(a => 
          a.assignment_id === assignmentId 
            ? { ...a, status: 'handed_over' }
            : a
        )
      );
      
      // Check if all handovers are complete
      if (response.data.all_handovers_complete) {
        toast.success('All handovers complete!');
        setTimeout(() => setStep('success'), 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm handover');
    } finally {
      setConfirmingAssignmentId(null);
    }
  };

  // Refresh queued status (check if capacity opened up)
  const refreshQueueStatus = async () => {
    try {
      setLoading(true);
      // First try to process the queue
      await apiClient.post('/wms/dispatch/pieces-processing/process-queue');
      
      // Then fetch picker's pending handovers
      const response = await apiClient.get(
        `/wms/dispatch/pieces-processing/picker-handovers/${staffId}`
      );
      
      if (response.data.pending_handovers && response.data.pending_handovers.length > 0) {
        // IMPORTANT: Filter to find handover for the CURRENT slip only
        const currentSlipNo = handoverSlipNo || currentSlip?.slip_no;
        const matchingHandover = response.data.pending_handovers.find(
          h => h.slip_no === currentSlipNo
        );
        
        if (matchingHandover) {
          // Found handover for current slip
          setIsQueued(false);
          setHandoverId(matchingHandover.handover_id);
          setHandoverAssignments(matchingHandover.assignments || []);
          toast.success('Capacity available! Ready for handover.');
        } else if (response.data.pending_handovers.length > 0) {
          // Has handovers but not for current slip - warn user
          const otherHandover = response.data.pending_handovers[0];
          toast.warning(`Found pending handover for different slip: ${otherHandover.slip_no}. Current slip: ${currentSlipNo}`);
          // Still check queued
          if (response.data.total_queued > 0) {
            toast.info('Current slip still waiting for capacity...');
          }
        } else {
          toast.info('Still waiting for capacity...');
        }
      } else if (response.data.total_queued > 0) {
        toast.info('Still waiting for capacity...');
      } else {
        // All done
        setStep('success');
      }
    } catch (error) {
      toast.error('Failed to refresh status');
    } finally {
      setLoading(false);
    }
  };

  // Handle combo bundle scanning
  const handleComboBundleScan = async () => {
    if (!scannedCode.trim()) {
      toast.error('Please scan bundle');
      vibrate([100, 50, 100]);
      return;
    }

    if (!currentComponent) {
      toast.error('No component selected');
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post(
        `/wms/combo-pick/scan-bundle?slip_group_id=${comboGroup.slip_group_id}&component_barcode=${currentComponent.component_barcode}&bundle_code=${scannedCode}`
      );
      
      vibrate([50, 50, 50]);
      toast.success(response.data.message);
      setScannedCode('');
      
      // Update component progress
      setComponentProgress(prev => ({
        ...prev,
        [currentComponent.component_barcode]: {
          picked: response.data.picked_bundles,
          total: currentComponent.required_bundles
        }
      }));
      
      // Update component in comboGroup
      setComboGroup(prev => ({
        ...prev,
        components: prev.components.map(c => 
          c.component_barcode === currentComponent.component_barcode
            ? { 
                ...c, 
                picked_bundles: response.data.picked_bundles, 
                status: response.data.component_status,
                bundles_to_pick: (c.bundles_to_pick || []).map(b =>
                  b.bundle_code === scannedCode.trim() ? { ...b, picked: true } : b
                )
              }
            : c
        )
      }));
      
      // Check if this component is now complete
      if (response.data.picked_bundles >= (currentComponent.required_bundles || 1)) {
        // Check if the ENTIRE group is complete (from backend, avoids stale state)
        if (response.data.group_completed) {
          // All components complete - show handover dialog
          setShowHandoverDialog(true);
        } else {
          // Find next incomplete component from fresh combo group state
          setComboGroup(prev => {
            const updated = prev.components.map(c => 
              c.component_barcode === currentComponent.component_barcode
                ? { ...c, picked_bundles: response.data.picked_bundles, status: 'COMPLETED' }
                : c
            );
            const nextIncomplete = updated.find(
              c => (c.picked_bundles || 0) < (c.required_bundles || 1) && c.status !== 'COMPLETED'
            );
            if (nextIncomplete) {
              setCurrentComponent(nextIncomplete);
              toast.info(`Moving to next component: ${nextIncomplete.component_name}`);
            } else {
              // Backend says not all done, but frontend can't find incomplete - show dialog anyway
              setShowHandoverDialog(true);
            }
            return { ...prev, components: updated };
          });
        }
      } else {
        // Update currentComponent with picked bundle marked
        setCurrentComponent(prev => ({
          ...prev,
          picked_bundles: response.data.picked_bundles,
          bundles_to_pick: (prev.bundles_to_pick || []).map(b =>
            b.bundle_code === scannedCode.trim() ? { ...b, picked: true } : b
          )
        }));
      }
      
      setTimeout(() => scanInputRef.current?.focus(), 100);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to scan bundle';
      
      // Handle "Component already fully picked" — auto-advance
      if (errorMsg.includes('already fully picked')) {
        toast.info('Component already picked — advancing...');
        // Mark current component as complete and find next
        const updatedComponents = comboGroup.components.map(c => 
          c.component_barcode === currentComponent.component_barcode
            ? { ...c, picked_bundles: c.required_bundles || 1, status: 'COMPLETED' }
            : c
        );
        setComboGroup(prev => ({ ...prev, components: updatedComponents }));
        setComponentProgress(prev => ({
          ...prev,
          [currentComponent.component_barcode]: {
            picked: currentComponent.required_bundles || 1,
            total: currentComponent.required_bundles || 1
          }
        }));
        
        const nextIncomplete = updatedComponents.find(
          c => (c.picked_bundles || 0) < (c.required_bundles || 1) && !c.all_bundles_already_picked
        );
        if (nextIncomplete) {
          setCurrentComponent(nextIncomplete);
          toast.info(`Moving to: ${nextIncomplete.component_name}`);
        } else {
          setCurrentComponent(null);
          setShowHandoverDialog(true);
        }
      } else {
        toast.error(errorMsg);
        vibrate([100, 50, 100]);
      }
      setScannedCode('');
      scanInputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Skip a combo bundle that's not in SFG storage
  const handleSkipComboBundle = async (bundle) => {
    if (!bundle?.bundle_code || !comboGroup?.slip_group_id || !currentComponent?.component_barcode) {
      toast.error('Cannot skip — missing info');
      return;
    }
    try {
      setLoading(true);
      const response = await apiClient.post(
        `/wms/combo-pick/skip-bundle?slip_group_id=${comboGroup.slip_group_id}&component_barcode=${currentComponent.component_barcode}&bundle_code=${bundle.bundle_code}`
      );

      toast.success('Bundle skipped');

      // Use backend as source of truth — avoids stale local state race conditions
      const newPicked = response.data.picked_bundles;
      const compStatus = response.data.component_status;
      const groupCompleted = response.data.group_completed === true;
      const skippedBarcode = currentComponent.component_barcode;

      // Mark auto-skip ref so the 5s polling doesn't re-fire for this bundle
      try { autoSkippedCodesRef.current.add(bundle.bundle_code); } catch (_) { /* noop */ }

      const updatedComponents = comboGroup.components.map(c => {
        if (c.component_barcode !== skippedBarcode) return c;
        return {
          ...c,
          picked_bundles: newPicked,
          status: compStatus,
          bundles_to_pick: (c.bundles_to_pick || []).map(b =>
            b.bundle_code === bundle.bundle_code ? { ...b, skipped: true } : b
          )
        };
      });
      setComboGroup(prev => ({ ...prev, components: updatedComponents }));
      setComponentProgress(prev => ({
        ...prev,
        [skippedBarcode]: {
          picked: newPicked,
          total: currentComponent.required_bundles || 1
        }
      }));

      // PRIMARY: backend-truth handover decision
      if (groupCompleted) {
        setCurrentComponent(null);
        setShowHandoverDialog(true);
        return;
      }

      if (compStatus === 'COMPLETED') {
        // This component is done but group isn't. Find next pickable component.
        const nextIncomplete = updatedComponents.find(
          c => (c.picked_bundles || 0) < (c.required_bundles || 1)
               && c.status !== 'COMPLETED'
               && !c.all_bundles_already_picked
        );
        if (nextIncomplete) {
          setCurrentComponent(nextIncomplete);
          toast.info(`Moving to: ${nextIncomplete.component_name}`);
        } else {
          // No remaining work locally either — show handover (server agrees implicitly)
          setCurrentComponent(null);
          setShowHandoverDialog(true);
        }
      } else {
        // Same component still IN_PROGRESS — advance to next bundle
        const currentUpdated = updatedComponents.find(c => c.component_barcode === skippedBarcode);
        setCurrentComponent(currentUpdated);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip bundle');
    } finally {
      setLoading(false);
    }
  };


  // Fetch Pieces Processing staff for handover
  // Complete combo picking — auto-allocates PP staff on backend
  const handleComboComplete = async () => {
    try {
      setLoading(true);
      const response = await apiClient.post(
        `/wms/combo-pick/complete-handover?slip_group_id=${comboGroup.slip_group_id}`
      );
      
      toast.success(response.data.message);
      setShowHandoverDialog(false);
      setHandoverInfo({
        staff_name: response.data.pp_staff?.name || '',
        staff_id: response.data.pp_staff?.id || '',
        total_pieces: response.data.total_bundles_picked || 0
      });
      setStep('success');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete handover');
    } finally {
      setLoading(false);
    }
  };

  // Mark component as unavailable
  const handleMarkUnavailable = async () => {
    if (!currentComponent) return;

    try {
      setLoading(true);
      const response = await apiClient.post(
        `/wms/combo-pick/mark-unavailable?slip_group_id=${comboGroup.slip_group_id}&component_barcode=${currentComponent.component_barcode}`
      );
      
      toast.warning(response.data.message);
      setShowUnavailableDialog(false);
      
      // Return to select-slip to get next task
      setStep('select-slip');
      setComboGroup(null);
      setCurrentComponent(null);
      setIsCombo(false);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark unavailable');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const progressPercentage = progress.total > 0 ? Math.round((progress.picked / progress.total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-cyan-600 to-cyan-800 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/wms/mobile')}
            className="text-white hover:bg-white/20 -ml-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold">SFG Picking</h1>
            {currentSlip && (
              <p className="text-sm text-cyan-200">{currentSlip.slip_no}</p>
            )}
          </div>
          <div className="w-10" />
        </div>
        
        {step === 'picking' && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span>{progress.picked} of {progress.total}</span>
              <span>{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-3 bg-cyan-900" />
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 flex flex-col">
        {/* Floor-Ready Notification Banner — bundles previously placed on
            floor have been auto-assigned to a free PP staff. Surface
            here on every screen except the staff-entry screen. */}
        {step !== 'staff' && floorReadyHandovers.length > 0 && (
          <div
            className="mb-4 rounded-2xl bg-gradient-to-r from-orange-600 to-orange-800 p-4 shadow-lg border border-orange-400"
            data-testid="sfg-floor-ready-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm uppercase tracking-wide">
                  {floorReadyHandovers.length} bundle batch{floorReadyHandovers.length > 1 ? 'es' : ''} ready for handover
                </h4>
                <p className="text-xs text-orange-100 mt-1">
                  Pieces Processing staff is now free. Walk over to the floor area and hand the bundles over before continuing.
                </p>
                <div className="mt-3 space-y-2">
                  {floorReadyHandovers.slice(0, 3).map((n) => (
                    <div
                      key={n.handover_id}
                      className="bg-black/30 rounded-lg p-2 text-xs"
                      data-testid={`sfg-floor-ready-row-${n.handover_id}`}
                    >
                      <div className="font-bold">
                        Slip #{n.slip_no} · {n.total_bundles} bundle(s) · {n.total_pieces} pcs
                      </div>
                      <div className="text-orange-200 text-[11px] mt-0.5">
                        {(n.assignments || []).map((a) => `${a.staff_name || a.staff_id} (${a.bundle_count}b)`).join(' · ')}
                      </div>
                      <button
                        type="button"
                        onClick={() => dismissFloorReadyNotification(n.handover_id)}
                        className="mt-1 text-[11px] underline text-white/80 hover:text-white"
                        data-testid={`sfg-floor-ready-dismiss-${n.handover_id}`}
                      >
                        I've handed these over — dismiss
                      </button>
                    </div>
                  ))}
                  {floorReadyHandovers.length > 3 && (
                    <div className="text-[11px] text-orange-100">
                      +{floorReadyHandovers.length - 3} more pending. Tap above to dismiss as you complete.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step: Staff Validation */}
        {step === 'staff' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto">
                <User className="w-10 h-10 text-cyan-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Staff ID</h2>
                <p className="text-gray-400">Scan your badge to start picking</p>
              </div>
              <Input
                id="staff-input"
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                placeholder="Staff ID"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl"
                data-testid="mobile-picking-staff-input"
              />
              <Button
                onClick={handleStaffValidate}
                disabled={loading || !staffId}
                className="w-full h-16 text-xl font-bold bg-cyan-600 hover:bg-cyan-700 rounded-xl"
              >
                {loading ? 'Validating...' : 'Continue'}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Select Slip - Now shows empty state only (no manual selection) */}
        {step === 'select-slip' && (
          <div className="flex-1 flex flex-col">
            {/* Pending Handover Queue - Actionable Tile */}
            {pendingQueueBundles.length > 0 && (
              <div className="bg-orange-900/50 border border-orange-600 rounded-xl p-4 mb-4" data-testid="pending-queue-tile">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                    <span className="text-orange-300 font-bold text-sm">
                      Handover Queue
                    </span>
                  </div>
                  <Badge variant="outline" className="text-orange-300 border-orange-500">
                    {pendingQueueTotal.bundles} bundle{pendingQueueTotal.bundles !== 1 ? 's' : ''} • {pendingQueueTotal.pieces} pcs
                  </Badge>
                </div>
                <div className="space-y-2">
                  {pendingQueueBundles.map((item, idx) => {
                    const qId = item.queue_id || item.handover_id;
                    return (
                      <div key={idx} className="bg-black/30 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <div>
                            <span className="text-xs font-mono text-orange-300">Slip: {item.slip_no}</span>
                            <span className="text-xs text-gray-400 ml-2">
                              {item.total_bundles} bundle{item.total_bundles !== 1 ? 's' : ''} • {item.total_pieces} pcs
                            </span>
                          </div>
                        </div>
                        {(item.picker_name || item.design_name) && (
                          <p className="text-xs text-gray-400 mb-1">
                            {item.picker_name && <span>Picker: {item.picker_name}</span>}
                            {item.picker_name && item.design_name && <span> • </span>}
                            {item.design_name && <span>{item.design_name}</span>}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1 mb-2">
                          {item.bundles?.slice(0, 4).map((bundle, bIdx) => (
                            <span key={bIdx} className="text-xs text-orange-200/80 bg-orange-950/50 px-1.5 py-0.5 rounded">
                              {bundle.bundle_code}
                            </span>
                          ))}
                          {item.bundles?.length > 4 && (
                            <span className="text-xs text-orange-400">+{item.bundles.length - 4} more</span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              setLoading(true);
                              const response = await apiClient.post(`/wms/dispatch/handover-queue/retry-assignment?queue_id=${qId}`);
                              if (response.data.success) {
                                if (response.data.assigned) {
                                  toast.success(`Assigned ${response.data.assigned_bundles || ''} bundles to staff`);
                                  // Load handover details so picker can confirm
                                  if (response.data.handover_id) {
                                    setHandoverId(response.data.handover_id);
                                    setHandoverSlipNo(item.slip_no);
                                    setHandoverAssignments(response.data.assignments || []);
                                    setIsQueued(false);
                                    setStep('handover');
                                  }
                                } else {
                                  toast.warning('Still no capacity available. Bundles remain queued.');
                                }
                                fetchPendingHandoverQueue();
                              }
                            } catch (error) {
                              toast.error('Failed to retry assignment');
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading}
                          className="w-full bg-orange-600 hover:bg-orange-700 text-white text-xs h-8"
                          data-testid={`retry-queue-${qId}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Retry Assignment
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mb-4">
                <Truck className="w-10 h-10 text-gray-500" />
              </div>
              <p className="text-gray-400 text-lg">No pending pick slips</p>
              <p className="text-sm text-gray-500 mt-2">Check back later for new assignments</p>
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={getNextAssignedSlip}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  disabled={loading}
                >
                  {loading ? 'Checking...' : 'Refresh'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/wms/mobile')}
                  className="border-gray-600"
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step: Picking - Recovery when no bundle loaded */}
        {step === 'picking' && !currentBundle && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-8 text-center space-y-6">
              <div className="w-24 h-24 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto">
                <AlertTriangle className="w-14 h-14 text-amber-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-amber-400">No Bundle Loaded</h2>
                <p className="text-gray-400 mt-2">
                  The picking screen could not load the next bundle.
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={async () => {
                    if (currentSlip?.slip_no) {
                      setLoading(true);
                      await fetchNextBundle(currentSlip.slip_no);
                      setLoading(false);
                    }
                  }}
                  className="w-full h-14 text-lg bg-cyan-600 hover:bg-cyan-700 rounded-xl"
                  disabled={loading}
                  data-testid="sfg-retry-bundle-btn"
                >
                  {loading ? 'Retrying...' : 'Retry Load Bundle'}
                </Button>
                <Button
                  onClick={async () => {
                    // Force complete and move to next slip
                    setCurrentSlip(null);
                    setCurrentBundle(null);
                    setProgress({ picked: 0, total: 0 });
                    setHandoverInfo(null);
                    setHandoverAssignments([]);
                    setHandoverId(null);
                    setIsQueued(false);
                    await getNextAssignedSlip();
                  }}
                  className="w-full h-14 text-lg bg-green-600 hover:bg-green-700 rounded-xl"
                  disabled={loading}
                  data-testid="sfg-skip-to-next-slip-btn"
                >
                  {loading ? 'Loading...' : 'Skip to Next Slip'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/wms/mobile')}
                  className="w-full h-14 border-gray-600 text-gray-300 rounded-xl"
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step: Picking */}
        {step === 'picking' && currentBundle && (
          <div className="flex-1 flex flex-col space-y-4">
            {/* Go To Location */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-5">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <MapPin className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-sm text-blue-200">Go to Location</p>
                  <p className="text-3xl font-bold">{currentBundle.bin_location}</p>
                </div>
              </div>
            </div>

            {/* Bundle to Pick */}
            <div className="bg-gray-800 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Package className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Pick Bundle</p>
                    <p className="font-mono font-bold text-lg" data-testid="bundle-code-display">{currentBundle.bundle_code}</p>
                    {currentBundle.is_alternate && (
                      <p className="text-xs text-amber-300 mt-0.5" data-testid="bundle-alternate-badge">
                        ⇄ Alternate for {currentBundle.replaces_bundle || 'a not-found bundle'}
                      </p>
                    )}
                    {bundleDesignName && (
                      <p className="text-sm text-cyan-300" data-testid="bundle-design-name">{bundleDesignName}</p>
                    )}
                    {(bundleShade || bundleSize) && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {bundleShade && <span data-testid="bundle-shade">Shade: <span className="text-gray-200">{bundleShade}</span></span>}
                        {bundleShade && bundleSize && <span> • </span>}
                        {bundleSize && <span data-testid="bundle-size">Size: <span className="text-gray-200">{bundleSize}</span></span>}
                      </p>
                    )}
                    {bundleDescription && (
                      <p className="text-xs text-gray-500 mt-0.5" data-testid="bundle-description">{bundleDescription}</p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold" data-testid="regular-bundle-pieces">{currentBundle.usn_count}</p>
                  <p className="text-xs text-gray-500">units</p>
                  {currentBundle.consumption_adjusted && currentBundle.original_usn_count > currentBundle.usn_count && (
                    <p className="text-[10px] text-amber-400 font-semibold mt-1" data-testid="regular-bundle-consumption-note">
                      {currentBundle.other_sessions_consumed || currentBundle.consumed_by_previous || 0} pcs used elsewhere
                      <span className="block text-amber-300/80">Orig: {currentBundle.original_usn_count}</span>
                    </p>
                  )}
                </div>
              </div>
              
              <div className="text-sm text-gray-400">
                Pick #{currentBundle.pick_sequence} of {progress.total}
              </div>
            </div>

            {/* Scan Input */}
            <div className="bg-gray-800 rounded-2xl p-5 space-y-4 flex-1">
              <div className="text-center">
                <h3 className="font-bold text-lg mb-1">Scan Bundle</h3>
                <p className="text-sm text-gray-400">Scan to confirm pickup</p>
              </div>
              <Input
                ref={scanInputRef}
                value={scannedCode}
                onChange={(e) => setScannedCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleBundlePick)}
                placeholder="Scan Bundle"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-picking-scan-input"
              />
              <Button
                onClick={handleBundlePick}
                disabled={loading || !scannedCode}
                className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-confirm-pick"
              >
                {loading ? 'Processing...' : 'Confirm Pick'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowSkipConfirm(true)}
                disabled={loading}
                className="w-full h-12 border-red-600 text-red-400 hover:bg-red-500/20 rounded-xl"
                data-testid="mobile-skip-bundle"
              >
                <XCircle className="w-5 h-5 mr-2" />
                Bundle Not Found
              </Button>
            </div>

            {/* Warning for wrong scan */}
            {scannedCode && scannedCode !== currentBundle.bundle_code && (
              <div className="flex items-center gap-3 p-4 bg-red-500/20 rounded-xl">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                <div>
                  <p className="font-medium text-red-400">Wrong Bundle!</p>
                  <p className="text-sm text-red-300">Expected: {currentBundle.bundle_code}</p>
                </div>
              </div>
            )}

            {/* Skip Confirmation Dialog */}
            <Dialog open={showSkipConfirm} onOpenChange={setShowSkipConfirm}>
              <DialogContent className="bg-gray-900 border-gray-700 text-white">
                <DialogHeader>
                  <DialogTitle className="text-red-400">Skip Bundle?</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    This will mark the bundle as "Not Found" and send it to the supervisor investigation queue.
                  </DialogDescription>
                </DialogHeader>
                <div className="p-4 bg-red-500/10 rounded-lg border border-red-800">
                  <p className="font-mono font-bold">{currentBundle?.bundle_code}</p>
                  <p className="text-sm text-gray-400 mt-1">Location: {currentBundle?.bin_location}</p>
                  {bundleDesignName && <p className="text-sm text-gray-400">Design: {bundleDesignName}</p>}
                  {(bundleShade || bundleSize) && (
                    <p className="text-sm text-gray-400">
                      {bundleShade && <>Shade: {bundleShade}</>}
                      {bundleShade && bundleSize && <> • </>}
                      {bundleSize && <>Size: {bundleSize}</>}
                    </p>
                  )}
                  {bundleDescription && <p className="text-xs text-gray-500">{bundleDescription}</p>}
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium text-red-300">
                    Remarks <span className="text-red-500">*</span> (required)
                  </label>
                  <textarea
                    value={skipRemarks}
                    onChange={(e) => setSkipRemarks(e.target.value)}
                    placeholder="Why is the bundle not found? e.g. bin empty, different bundle in bin…"
                    rows={3}
                    className="w-full rounded-lg bg-gray-800 border border-gray-600 p-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-red-500"
                    data-testid="skip-remarks-input"
                  />
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowSkipConfirm(false)} className="border-gray-600">
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleSkipBundle}
                    disabled={loading || !skipRemarks.trim()}
                    data-testid="confirm-skip-btn"
                  >
                    {loading ? 'Skipping...' : 'Confirm Skip'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Step: Combo Picking */}
        {step === 'combo-picking' && comboGroup && (
          <div className="flex-1 flex flex-col gap-4 pb-20">
            {/* Combo Header */}
            <div className="bg-gradient-to-r from-orange-600 to-orange-800 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Boxes className="w-8 h-8 text-orange-200" />
                  <div>
                    <Badge className="bg-orange-400 text-orange-900 mb-1">COMBO</Badge>
                    <p className="font-bold text-lg">{comboGroup.combo_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{comboGroup.target_quantity}</p>
                  <p className="text-xs text-orange-200">combos</p>
                </div>
              </div>
            </div>

            {/* Component Progress */}
            <div className="bg-gray-800 rounded-2xl p-4 space-y-3">
              <h3 className="font-bold text-sm text-gray-400 uppercase">Components</h3>
              {comboGroup.components?.map((comp, idx) => {
                const isComplete = (comp.picked_bundles || 0) >= (comp.required_bundles || comp.required_qty || 1);
                const isAlreadyPicked = comp.all_bundles_already_picked && !isComplete;
                const isExpanded = !!expandedAlreadyPicked[comp.component_barcode];
                return (
                <div
                  key={comp.component_barcode}
                  className={`rounded-xl transition-all ${
                    currentComponent?.component_barcode === comp.component_barcode
                      ? 'bg-cyan-600/30 border-2 border-cyan-500'
                      : isComplete
                        ? 'bg-green-600/20 border border-green-500'
                        : isAlreadyPicked
                          ? 'bg-amber-600/20 border border-amber-500'
                          : 'bg-gray-700/50 border border-transparent'
                  }`}
                >
                <div
                  className={`flex items-center justify-between p-3 ${isComplete ? '' : 'cursor-pointer'}`}
                  onClick={() => {
                    if (isComplete) return;
                    if (isAlreadyPicked) {
                      setExpandedAlreadyPicked((prev) => ({
                        ...prev,
                        [comp.component_barcode]: !prev[comp.component_barcode],
                      }));
                    } else {
                      setCurrentComponent(comp);
                    }
                  }}
                  data-testid={`combo-component-row-${comp.component_barcode}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isComplete ? 'bg-green-500' : isAlreadyPicked ? 'bg-amber-500' : 'bg-gray-600'
                    }`}>
                      {isComplete ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : isAlreadyPicked ? (
                        <CheckCircle className="w-5 h-5 text-white" />
                      ) : (
                        <span className="font-bold">{idx + 1}</span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{comp.component_name}</p>
                      <p className="text-xs text-gray-400">{comp.component_barcode}</p>
                      {isAlreadyPicked && (
                        <p className="text-xs text-amber-400 font-medium mt-0.5">
                          Picked by another session — tap to see bundles
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">
                      {componentProgress[comp.component_barcode]?.picked || 0}
                      <span className="text-gray-400">/{comp.required_bundles}</span>
                    </p>
                    <p className="text-xs text-gray-400">bundles</p>
                  </div>
                </div>

                {/* Expanded bundle details for "already picked" component */}
                {isAlreadyPicked && isExpanded && (
                  <div className="border-t border-amber-600/40 px-3 py-2 space-y-2 bg-amber-950/30 rounded-b-xl" data-testid={`already-picked-details-${comp.component_barcode}`}>
                    <p className="text-[11px] text-amber-200/80 uppercase font-semibold">Bundle trace — where each piece is</p>
                    {(comp.bundles_to_pick || []).length === 0 && (
                      <p className="text-xs text-amber-300/70">No bundle info returned by backend. Contact supervisor with combo session id.</p>
                    )}
                    {(comp.bundles_to_pick || []).map((b) => {
                      const loc = b.location_status;
                      const locLabel = loc === 'with_pp_staff'
                        ? `With PP: ${b.with_pp_staff_name || b.with_pp_staff_id || 'unknown'}`
                        : loc === 'in_sfg'
                          ? `In SFG${b.bin_location ? ` — Bin ${b.bin_location}` : ''}`
                          : loc === 'not_in_sfg'
                            ? 'Not in SFG (consumed / in WIP)'
                            : 'Unknown location';
                      const pickedByLabel = b.picked_by_staff_name || b.picked_by_staff_id || '—';
                      const pickedAt = b.picked_at_ts ? b.picked_at_ts.slice(0, 16).replace('T', ' ') : '';
                      const available = b.available_usn_count;
                      const original = b.original_usn_count;
                      return (
                        <div
                          key={b.bundle_code}
                          className="rounded-lg bg-amber-900/30 border border-amber-700/40 p-2 text-xs"
                          data-testid={`already-picked-bundle-${b.bundle_code}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-mono text-amber-200 text-[11px] font-bold truncate">{b.bundle_code || '(no bundle code)'}</div>
                            <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded ${
                              loc === 'with_pp_staff' ? 'bg-blue-500/30 text-blue-100'
                              : loc === 'in_sfg' ? 'bg-green-500/30 text-green-100'
                              : 'bg-gray-500/30 text-gray-100'
                            }`}>
                              {loc === 'with_pp_staff' ? 'With PP' : loc === 'in_sfg' ? 'In SFG' : 'Gone'}
                            </span>
                          </div>
                          <div className="mt-1 text-amber-100/80">
                            {locLabel}
                          </div>
                          <div className="mt-0.5 text-amber-200/60 text-[11px]">
                            Picked by: <span className="text-amber-100">{pickedByLabel}</span>
                            {pickedAt ? ` · ${pickedAt}` : ''}
                          </div>
                          {typeof available === 'number' && typeof original === 'number' && available !== original && (
                            <div className="mt-0.5 text-amber-200/60 text-[11px]">
                              Pieces: <span className="text-amber-100 font-semibold">{available}</span>
                              <span className="text-amber-300/50"> (orig {original}, {b.other_sessions_consumed || 0} used elsewhere)</span>
                            </div>
                          )}
                          {b.from_session_id && (
                            <div className="text-amber-200/50 text-[10px] font-mono truncate">Session: {b.from_session_id}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
                );
              })}
            </div>

            {/* Some Components Already Picked by Other Staff - Show Skip Button */}
            {comboGroup.components?.length > 0 &&
              comboGroup.components.every(c => 
                (c.all_bundles_already_picked && (c.picked_bundles || 0) < (c.required_bundles || c.required_qty || 1)) ||
                ((c.picked_bundles || 0) >= (c.required_bundles || c.required_qty || 1))
              ) &&
              comboGroup.components.some(c => c.all_bundles_already_picked && (c.picked_bundles || 0) < (c.required_bundles || c.required_qty || 1)) && (
              <div className="bg-amber-900/30 border border-amber-500 rounded-2xl p-5 space-y-4">
                <div className="text-center space-y-2">
                  <CheckCircle className="w-12 h-12 text-amber-400 mx-auto" />
                  <h3 className="font-bold text-lg text-amber-300">Already Picked</h3>
                  <p className="text-sm text-amber-200/70">
                    All components for this combo are already picked and with Pieces Processing staff. Collect from them directly.
                  </p>
                  {(() => {
                    const staffNames = [...new Set(
                      comboGroup.components
                        .flatMap(c => (c.bundles_to_pick || []))
                        .filter(b => b.with_pp_staff_name)
                        .map(b => b.with_pp_staff_name)
                    )];
                    if (staffNames.length > 0) {
                      return (
                        <div className="mt-2 space-y-1">
                          {staffNames.map(name => (
                            <p key={name} className="text-base font-bold text-amber-300">{name}</p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <Button
                  onClick={async () => {
                    try {
                      setLoading(true);
                      // Complete ALL assigned combo groups for this picker + this specific group
                      await apiClient.post(
                        `/wms/combo-pick/complete-group?slip_group_id=${comboGroup.slip_group_id}&staff_id=${encodeURIComponent(staffId)}&skip_reason=all_already_picked`
                      );
                      setComboGroup(null);
                      setCurrentComponent(null);
                      setIsCombo(false);
                      setStep('staff');
                      toast.info('Skipped — moving to next task');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to skip');
                      // Force skip even if backend fails
                      setComboGroup(null);
                      setCurrentComponent(null);
                      setIsCombo(false);
                      setStep('staff');
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  className="w-full h-14 text-lg font-bold bg-amber-600 hover:bg-amber-700 rounded-xl"
                  data-testid="skip-already-picked-btn"
                >
                  {loading ? 'Skipping...' : 'Skip to Next Task'}
                </Button>
              </div>
            )}

            {/* All Components Fully Picked — Show Complete Handover */}
            {comboGroup.components?.length > 0 && 
              comboGroup.components.every(c => (c.picked_bundles || 0) >= (c.required_bundles || c.required_qty || 1)) && 
              !comboGroup.components.every(c => c.all_bundles_already_picked && (c.picked_bundles || 0) < (c.required_bundles || c.required_qty || 1)) && (
              <div className="bg-green-900/30 border border-green-500 rounded-2xl p-5 space-y-4">
                <div className="text-center space-y-2">
                  <CheckCircle className="w-14 h-14 text-green-400 mx-auto" />
                  <h3 className="font-bold text-xl text-green-300">All Components Picked!</h3>
                  <p className="text-sm text-green-200/70">
                    All bundles have been picked. Complete the handover to assign a Pieces Processing staff.
                  </p>
                </div>
                <Button 
                  onClick={handleComboComplete} 
                  disabled={loading} 
                  className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                  data-testid="combo-complete-handover-btn"
                >
                  {loading ? 'Processing...' : 'Complete Handover'}
                </Button>
              </div>
            )}

            {/* Current Component Scan */}
            {currentComponent && (currentComponent.picked_bundles || 0) < (currentComponent.required_bundles || currentComponent.required_qty || 1) && (
              <div className="bg-gray-800 rounded-2xl p-5 space-y-4 flex-1">
                <div className="text-center">
                  <h3 className="font-bold text-lg mb-1">Scan {currentComponent.component_name}</h3>
                  <p className="text-sm text-gray-400">
                    Pick {(componentProgress[currentComponent.component_barcode]?.picked || 0) + 1} of {currentComponent.required_bundles} bundles
                  </p>
                </div>

                {/* Bundle to pick info */}
                {(() => {
                  const bundles = currentComponent.bundles_to_pick || [];
                  const pickedCount = componentProgress[currentComponent.component_barcode]?.picked || 0;
                  // First try the positional next bundle, but reject if it's already picked/skipped
                  const positional = bundles[pickedCount];
                  const positionalValid = positional && !positional.picked && !positional.skipped;
                  // Otherwise find the first non-picked-non-skipped bundle. Fall back to null rather
                  // than bundles[0] — showing bundles[0] (already processed) caused a "ghost" Skip
                  // card that froze the UI after the last bundle was skipped.
                  const nextBundle = positionalValid
                    ? positional
                    : bundles.find(b => !b.picked && !b.skipped);
                  if (!nextBundle) return null;

                  if (nextBundle.location_status === 'with_pp_staff') {
                    // Bundle already picked by another session — with PP Staff
                    return (
                      <div className="bg-amber-900/40 border border-amber-500 rounded-xl p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-amber-300 uppercase font-medium">With PP Staff</span>
                          <span className="text-xs text-amber-300">Collect from</span>
                        </div>
                        <p className="text-2xl font-bold text-amber-400 text-center" data-testid="combo-pp-staff-name">
                          {nextBundle.with_pp_staff_name || 'PP Staff'}
                        </p>
                        <div className="flex items-center justify-between pt-2 border-t border-amber-700">
                          <div>
                            <span className="text-xs text-amber-300/70">Bundle Code</span>
                            <p className="font-mono font-bold text-base" data-testid="combo-bundle-code">{nextBundle.bundle_code}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-amber-300/70">Pieces</span>
                            <p className="text-xl font-bold">{nextBundle.usn_count}</p>
                          </div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button
                            onClick={() => handleSkipComboBundle(nextBundle)}
                            disabled={loading}
                            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl h-12 font-bold"
                            data-testid="skip-pp-staff-bundle-btn"
                          >
                            {loading ? 'Skipping...' : 'Skip Bundle'}
                          </Button>
                        </div>
                      </div>
                    );
                  }

                  if (nextBundle.location_status === 'not_in_sfg') {
                    // Bundle not in SFG and not tracked to another session
                    return (
                      <div className="bg-red-900/40 border border-red-500 rounded-xl p-4 space-y-2">
                        <div className="text-center">
                          <span className="text-xs text-red-300 uppercase font-medium">Not in SFG Storage</span>
                          <p className="text-sm text-red-200/70 mt-1">This bundle has been moved out of SFG storage.</p>
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-red-700">
                          <div>
                            <span className="text-xs text-red-300/70">Bundle Code</span>
                            <p className="font-mono font-bold text-base">{nextBundle.bundle_code}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-red-300/70">Pieces</span>
                            <p className="text-xl font-bold">{nextBundle.usn_count}</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => handleSkipComboBundle(nextBundle)}
                          disabled={loading}
                          className="w-full mt-2 bg-red-600 hover:bg-red-700 text-white rounded-xl h-12 font-bold"
                          data-testid="skip-combo-bundle-btn"
                        >
                          {loading ? 'Skipping...' : 'Skip Bundle'}
                        </Button>
                      </div>
                    );
                  }

                  // Normal: bundle is in SFG storage
                  return (
                    <div className="bg-gray-700/60 rounded-xl p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 uppercase font-medium">Bin Location</span>
                        <span className="text-xs text-gray-400">Pick from</span>
                      </div>
                      <p className="text-3xl font-bold text-cyan-400 text-center" data-testid="combo-bin-location">
                        {nextBundle.bin_location || 'N/A'}
                      </p>
                      {(nextBundle.design_name || nextBundle.shade || nextBundle.size || nextBundle.description) && (
                        <div className="pt-2 border-t border-gray-600 space-y-0.5">
                          {nextBundle.design_name && (
                            <p className="text-sm text-cyan-300" data-testid="combo-bundle-design-name">
                              {nextBundle.design_name}
                            </p>
                          )}
                          {(nextBundle.shade || nextBundle.size) && (
                            <p className="text-xs text-gray-300">
                              {nextBundle.shade && <span data-testid="combo-bundle-shade">Shade: <span className="text-white">{nextBundle.shade}</span></span>}
                              {nextBundle.shade && nextBundle.size && <span> • </span>}
                              {nextBundle.size && <span data-testid="combo-bundle-size">Size: <span className="text-white">{nextBundle.size}</span></span>}
                            </p>
                          )}
                          {nextBundle.description && (
                            <p className="text-xs text-gray-400" data-testid="combo-bundle-description">{nextBundle.description}</p>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between pt-2 border-t border-gray-600">
                        <div>
                          <span className="text-xs text-gray-400">Bundle Code</span>
                          <p className="font-mono font-bold text-base" data-testid="combo-bundle-code">{nextBundle.bundle_code}</p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs text-gray-400">Pieces</span>
                          <p className="text-xl font-bold" data-testid="combo-bundle-pieces">{nextBundle.usn_count}</p>
                          {nextBundle.consumption_adjusted && nextBundle.original_usn_count > nextBundle.usn_count && (
                            <p className="text-[10px] text-amber-400 font-semibold" data-testid="combo-bundle-consumption-note">
                              {nextBundle.other_sessions_consumed} pcs used by other sessions
                              <span className="block text-amber-300/80">
                                Original: {nextBundle.original_usn_count}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <Input
                  ref={scanInputRef}
                  value={scannedCode}
                  onChange={(e) => setScannedCode(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handleComboBundleScan)}
                  placeholder="Scan Bundle"
                  className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                  data-testid="combo-scan-input"
                />
                <Button
                  onClick={handleComboBundleScan}
                  disabled={loading || !scannedCode}
                  className="w-full h-14 text-lg font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                >
                  {loading ? 'Processing...' : 'Confirm Pick'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowUnavailableDialog(true)}
                  className="w-full h-12 text-red-400 border-red-400 hover:bg-red-500/20"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Component Not Available
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Unavailable Dialog */}
        <Dialog open={showUnavailableDialog} onOpenChange={setShowUnavailableDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-500">Mark Component Unavailable?</DialogTitle>
              <DialogDescription>
                This will cancel the combo picking task. Any already-picked bundles will be returned to SFG storage.
              </DialogDescription>
            </DialogHeader>
            {currentComponent && (
              <div className="p-4 bg-red-500/10 rounded-lg">
                <p className="font-medium">{currentComponent.component_name}</p>
                <p className="text-sm text-gray-500">{currentComponent.component_barcode}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUnavailableDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleMarkUnavailable} disabled={loading}>
                {loading ? 'Processing...' : 'Mark Unavailable'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Handover Dialog — Auto-allocates PP Staff */}
        <Dialog open={showHandoverDialog} onOpenChange={setShowHandoverDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>All Components Picked!</DialogTitle>
              <DialogDescription>
                The system will automatically assign a Pieces Processing staff for handover.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHandoverDialog(false)}>Cancel</Button>
              <Button onClick={handleComboComplete} disabled={loading} data-testid="auto-handover-btn">
                {loading ? 'Processing...' : 'Complete Handover'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Step: Multi-Staff Handover */}
        {step === 'handover' && (
          <div className="flex-1 flex flex-col gap-4 pb-20">
            {/* Handover Header */}
            <div className={`rounded-2xl p-5 ${isQueued ? 'bg-gradient-to-r from-amber-600 to-amber-800' : 'bg-gradient-to-r from-green-600 to-green-800'}`}>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  {isQueued ? <Clock className="w-7 h-7" /> : <Users className="w-7 h-7" />}
                </div>
                <div>
                  <p className="text-sm opacity-80">
                    {isQueued ? 'Bundles Queued' : 'Hand Over Bundles'}
                  </p>
                  <p className="text-2xl font-bold">
                    {isQueued 
                      ? 'Waiting for Capacity' 
                      : `${handoverAssignments.length} Staff Member${handoverAssignments.length !== 1 ? 's' : ''}`
                    }
                  </p>
                  {/* Show current slip number for clarity */}
                  <p className="text-xs opacity-70 mt-1">
                    Slip: {handoverSlipNo || currentSlip?.slip_no || 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Warning: Other pending handovers exist */}
            {hasOtherPendingHandovers && (
              <div className="bg-yellow-900/50 border border-yellow-600 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-yellow-300 font-medium text-sm">Other Pending Handovers</p>
                  <p className="text-yellow-200/70 text-xs mt-1">
                    You have incomplete handovers from previous slips. Please complete those after this one.
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearCompletedHandovers}
                  disabled={loading}
                  className="border-yellow-600 text-yellow-300 hover:bg-yellow-600/20 text-xs"
                  data-testid="clear-handovers-btn"
                >
                  Clear Completed
                </Button>
              </div>
            )}
            
            {/* Warning: Some bundles could not be assigned */}
            {unassignedBundles.length > 0 && !isQueued && (
              <div className="bg-orange-900/50 border border-orange-600 rounded-xl p-4" data-testid="unassigned-bundles-warning">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-orange-300 font-medium text-sm">
                      {unassignedBundles.length} Bundle{unassignedBundles.length !== 1 ? 's' : ''} Queued
                    </p>
                    <p className="text-orange-200/70 text-xs mt-1">
                      These bundles couldn't be assigned due to staff capacity limits. They are queued and will be auto-assigned when capacity opens.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {unassignedBundles.slice(0, 5).map((bundle, idx) => (
                        <Badge key={idx} variant="outline" className="text-orange-300 border-orange-600 text-xs">
                          {bundle.bundle_code} ({bundle.usn_count} pcs)
                        </Badge>
                      ))}
                      {unassignedBundles.length > 5 && (
                        <Badge variant="outline" className="text-orange-300 border-orange-600 text-xs">
                          +{unassignedBundles.length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Queued State — bundles must be physically placed on floor */}
            {isQueued && (
              <div className="bg-gray-800 rounded-2xl p-6 text-center space-y-4" data-testid="sfg-floor-placement-card">
                <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto">
                  <Clock className="w-8 h-8 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-amber-300">All Staff at Full Capacity</h3>
                  <p className="text-gray-300 text-sm mt-2">
                    {floorPlacementConfirmed
                      ? 'Placement confirmed. You will be notified the next time you log in once a Pieces Processing staff is free. You may proceed to the next slip.'
                      : 'No Pieces Processing staff is free right now. Please place these bundles on the marked floor area and confirm below — you can then continue with the next slip.'}
                  </p>
                </div>

                {!floorPlacementConfirmed ? (
                  <Button
                    onClick={handleConfirmFloorPlacement}
                    disabled={floorPlacementSubmitting || !queuedHandoverQueueId}
                    className="w-full h-14 bg-amber-600 hover:bg-amber-700 rounded-xl text-base font-bold"
                    data-testid="sfg-confirm-floor-placement-btn"
                  >
                    {floorPlacementSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Confirming…
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Confirm Bundles Placed on Floor
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      // Picker now free to grab the next slip.
                      setStep('success');
                    }}
                    className="w-full h-14 bg-green-600 hover:bg-green-700 rounded-xl text-base font-bold"
                    data-testid="sfg-floor-placement-next-slip-btn"
                  >
                    <ChevronRight className="w-5 h-5 mr-2" />
                    Proceed to Next Slip
                  </Button>
                )}

                <Button
                  onClick={refreshQueueStatus}
                  disabled={loading || floorPlacementSubmitting}
                  variant="outline"
                  className="w-full h-12 border-amber-600 text-amber-300 rounded-xl"
                  data-testid="sfg-refresh-queue-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Checking…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry — Check for Free Staff Now
                    </>
                  )}
                </Button>

                <Button
                  variant="outline"
                  onClick={fetchCapacityInfo}
                  disabled={loading}
                  className="w-full h-12 border-cyan-600 text-cyan-300 rounded-xl"
                  data-testid="view-capacity-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  View Staff Capacity
                </Button>
              </div>
            )}

            {/* Staff Assignments List */}
            {!isQueued && handoverAssignments.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-bold text-gray-400 uppercase text-sm px-1">
                  Confirm Each Handover
                </h3>
                {handoverAssignments.map((assignment, idx) => (
                  <div 
                    key={assignment.assignment_id}
                    className={`bg-gray-800 rounded-2xl p-4 border-2 ${
                      assignment.status === 'handed_over' 
                        ? 'border-green-500 bg-green-900/20' 
                        : 'border-transparent'
                    }`}
                    data-testid={`handover-assignment-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          assignment.status === 'handed_over' ? 'bg-green-500' : 'bg-cyan-600'
                        }`}>
                          {assignment.status === 'handed_over' 
                            ? <CheckCircle className="w-5 h-5 text-white" />
                            : <User className="w-5 h-5 text-white" />
                          }
                        </div>
                        <div>
                          <p className="font-bold text-lg">{assignment.staff_name}</p>
                          <p className="text-xs text-gray-400">ID: {assignment.staff_id}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-cyan-400">{assignment.total_pieces}</p>
                        <p className="text-xs text-gray-500">pieces</p>
                      </div>
                    </div>
                    
                    {/* Bundle list */}
                    <div className="bg-gray-900/50 rounded-xl p-3 mb-3">
                      <p className="text-xs text-gray-500 mb-2">Bundles ({assignment.bundles?.length || 0})</p>
                      <div className="flex flex-wrap gap-2">
                        {assignment.bundles?.slice(0, 5).map((bundle, bIdx) => (
                          <Badge 
                            key={bIdx} 
                            variant="outline" 
                            className="text-xs text-white bg-gray-800 border-gray-600"
                          >
                            {typeof bundle === 'string' ? bundle : bundle.bundle_code}
                            {bundle.usn_count > 0 && ` (${bundle.usn_count})`}
                          </Badge>
                        ))}
                        {assignment.bundles?.length > 5 && (
                          <Badge variant="outline" className="text-xs text-white bg-gray-800 border-gray-600">
                            +{assignment.bundles.length - 5} more
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Confirm Button */}
                    {assignment.status !== 'handed_over' ? (
                      <Button
                        onClick={() => confirmHandoverToStaff(assignment.assignment_id)}
                        disabled={confirmingAssignmentId === assignment.assignment_id}
                        className="w-full h-12 bg-green-600 hover:bg-green-700 rounded-xl"
                        data-testid={`confirm-handover-${idx}`}
                      >
                        {confirmingAssignmentId === assignment.assignment_id ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Confirming...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Confirm Handover
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="flex items-center justify-center gap-2 text-green-400 py-2">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Handed Over</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* All Done - Continue Button */}
            {!isQueued && handoverAssignments.every(a => a.status === 'handed_over') && handoverAssignments.length > 0 && (
              <div className="mt-4">
                <Button
                  onClick={() => setStep('success')}
                  className="w-full h-14 bg-cyan-600 hover:bg-cyan-700 rounded-xl text-lg"
                >
                  Continue
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step: Combo V2 Distributor Handover
            ---------------------------------------------------------------
            V2 slips never go to regular Pieces-Processing staff. The picker
            walks the bundles to the floor staging zone where the Piece
            Distributor (using /wms/combo-v2/distributor/{plan_id}) allocates
            them to PP_COMBO staff. Bundles are already tagged
            PICKED_FOR_COMBO_V2 in the backend short-circuit. */}
        {step === 'combo-v2-handover' && comboV2Handover && (
          <div className="flex-1 flex flex-col gap-4 pb-20">
            <div className="rounded-2xl p-5 bg-gradient-to-r from-purple-700 to-fuchsia-700">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Users className="w-7 h-7" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm opacity-80">Hand Over Bundles To</p>
                  <p className="text-2xl font-bold">Piece Distributor</p>
                  <p
                    className="text-base font-semibold mt-1 truncate"
                    data-testid="v2-handover-plan-name"
                    title={comboV2Handover.plan_name || comboV2Handover.bulk_plan_id || '-'}
                  >
                    Plan: {comboV2Handover.plan_name
                      || comboV2Handover.bulk_plan_id
                      || '—'}
                  </p>
                  {comboV2Handover.plan_name && comboV2Handover.bulk_plan_id && (
                    <p className="text-xs opacity-70 font-mono mt-0.5">
                      ID: {comboV2Handover.bulk_plan_id}
                    </p>
                  )}
                  <p className="text-xs opacity-70 mt-0.5">
                    Slip: {comboV2Handover.slip_no || currentSlip?.slip_no || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-fuchsia-900/20 border border-fuchsia-700 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Badge className="bg-fuchsia-500 text-white">COMBO V2</Badge>
                <p className="text-sm text-fuchsia-200">
                  These bundles are <strong>not</strong> assigned to Pieces-Processing staff.
                  Carry them to the Combo V2 floor zone and hand them over to the
                  <strong> Piece Distributor</strong>, who will allocate them to PP_COMBO staff
                  via the distributor station.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-800 rounded-xl p-4">
                <p className="text-xs text-gray-400 uppercase">Bundles</p>
                <p className="text-3xl font-bold text-fuchsia-300">{comboV2Handover.total_bundles}</p>
              </div>
              <div className="bg-gray-800 rounded-xl p-4">
                <p className="text-xs text-gray-400 uppercase">Pieces</p>
                <p className="text-3xl font-bold text-fuchsia-300">{comboV2Handover.total_pieces}</p>
              </div>
            </div>

            {comboV2Handover.bundles && comboV2Handover.bundles.length > 0 && (
              <div className="bg-gray-800 rounded-xl p-3">
                <p className="text-xs text-gray-400 uppercase mb-2">Bundle List</p>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {comboV2Handover.bundles.map((b) => (
                    <div
                      key={b.bundle_code}
                      className="flex items-center justify-between text-sm py-1 border-b border-gray-700"
                      data-testid={`v2-bundle-${b.bundle_code}`}
                    >
                      <span className="font-mono text-gray-200">{b.bundle_code}</span>
                      <span className="text-fuchsia-300">{b.usn_count} pcs</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3 mt-auto">
              {/* Explicit confirm checkbox — picker must tick this BEFORE
                  the Confirm Handover button enables. Prevents accidental
                  "next slip" taps when bundles haven't physically moved. */}
              <label
                className="flex items-start gap-3 bg-fuchsia-900/30 border border-fuchsia-600 rounded-xl p-4 cursor-pointer"
                htmlFor="v2-handover-confirm-cb"
                data-testid="v2-handover-confirm-checkbox-row"
              >
                <input
                  id="v2-handover-confirm-cb"
                  type="checkbox"
                  className="mt-1 w-5 h-5 accent-fuchsia-500"
                  checked={comboV2HandoverConfirmed}
                  onChange={(e) => setComboV2HandoverConfirmed(e.target.checked)}
                  data-testid="v2-handover-confirm-checkbox"
                />
                <span className="text-sm text-fuchsia-100 leading-tight">
                  I have <strong>physically handed over</strong> all
                  {' '}{comboV2Handover.total_bundles} bundle(s) to the
                  Piece Distributor for plan
                  {' '}<strong>{comboV2Handover.plan_name || comboV2Handover.bulk_plan_id || 'Combo V2'}</strong>.
                </span>
              </label>

              <Button
                onClick={async () => {
                  if (!comboV2HandoverConfirmed || comboV2HandoverSubmitting) return;
                  setComboV2HandoverSubmitting(true);
                  try {
                    // Audit-trail the picker handover so the Distributor
                    // station can show "received from picker [staff]" with
                    // a timestamp.
                    await apiClient.post('/wms/dispatch/combo-v2/picker-handover/confirm', {
                      slip_no: comboV2Handover.slip_no || currentSlip?.slip_no,
                      bulk_plan_id: comboV2Handover.bulk_plan_id,
                      picker_staff_id: staffId,
                      picker_staff_name: staffName,
                      total_bundles: comboV2Handover.total_bundles,
                      total_pieces: comboV2Handover.total_pieces,
                      bundles: comboV2Handover.bundles,
                    });
                    toast.success('Handover confirmed');
                  } catch (err) {
                    // Non-blocking: even if audit fails we let the picker
                    // continue (the bundles are already PICKED_FOR_COMBO_V2).
                    console.error('Picker handover confirm failed:', err);
                    toast.warning('Handover audit log skipped (continuing)');
                  }
                  setComboV2Handover(null);
                  setComboV2HandoverConfirmed(false);
                  setComboV2HandoverSubmitting(false);
                  setCurrentSlip(null);
                  setCurrentBundle(null);
                  setProgress({ picked: 0, total: 0 });
                  setStep('select-slip');
                  getNextAssignedSlip();
                }}
                className="w-full h-14 text-lg bg-fuchsia-600 hover:bg-fuchsia-700 disabled:bg-fuchsia-900 disabled:opacity-60 rounded-xl"
                disabled={loading || !comboV2HandoverConfirmed || comboV2HandoverSubmitting}
                data-testid="v2-handover-done-btn"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                {comboV2HandoverSubmitting ? 'Confirming…' : 'Confirm Handover · Get Next Slip'}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/wms/mobile')}
                className="w-full h-14 border-gray-600 text-gray-300 rounded-xl"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-8 text-center space-y-6">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-400">Slip Complete!</h2>
                <p className="text-gray-400 mt-2">
                  {progress.total} bundles picked
                </p>
              </div>
              
              {/* Multi-Staff Handover Summary */}
              {handoverAssignments.length > 0 && (
                <div className="bg-green-500/20 border-2 border-green-500 rounded-2xl p-4">
                  <p className="text-green-300 text-sm uppercase font-bold mb-2">Handover Complete</p>
                  <div className="space-y-2">
                    {handoverAssignments.map((assignment, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-white">{assignment.staff_name}</span>
                        <span className="text-green-300">{assignment.total_pieces} pieces</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Legacy Single Handover Info */}
              {handoverInfo && handoverAssignments.length === 0 && (
                <div className="bg-amber-500/20 border-2 border-amber-500 rounded-2xl p-4">
                  <p className="text-amber-300 text-sm uppercase font-bold mb-1">Hand Over Bundles To</p>
                  <p className="text-2xl font-bold text-white">{handoverInfo.staff_name}</p>
                  <p className="text-amber-300 text-sm mt-1">
                    Staff ID: {handoverInfo.staff_id}{handoverInfo.total_pieces > 0 ? ` · ${handoverInfo.total_pieces} bundles` : ''}
                  </p>
                </div>
              )}
              
              {!handoverInfo && handoverAssignments.length === 0 && (
                <div className="bg-gray-700/50 rounded-2xl p-4">
                  <p className="text-gray-400 text-sm">Handover not required</p>
                </div>
              )}
              
              <div className="space-y-3">
                <Button
                  onClick={async () => {
                    setCurrentSlip(null);
                    setCurrentBundle(null);
                    setProgress({ picked: 0, total: 0 });
                    setHandoverInfo(null);
                    // Reset multi-staff handover states
                    setHandoverAssignments([]);
                    setHandoverId(null);
                    setIsQueued(false);
                    // Auto-assign next slip
                    await getNextAssignedSlip();
                  }}
                  className="w-full h-14 text-lg bg-cyan-600 hover:bg-cyan-700 rounded-xl"
                  disabled={loading}
                  data-testid="sfg-get-next-slip-btn"
                >
                  {loading ? 'Getting Next Slip...' : 'Get Next Slip'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/wms/mobile')}
                  className="w-full h-14 border-gray-600 text-gray-300 rounded-xl"
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Capacity Modal */}
      {showCapacityModal && capacityInfo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-gray-900 p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold">Staff Capacity</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowCapacityModal(false)}
                className="text-gray-400"
              >
                ✕
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Status Banner */}
              <div className={`p-3 rounded-xl ${
                capacityInfo.status === 'OK' 
                  ? 'bg-green-900/30 border border-green-600' 
                  : capacityInfo.status === 'NO_STAFF_AVAILABLE'
                  ? 'bg-red-900/30 border border-red-600'
                  : 'bg-amber-900/30 border border-amber-600'
              }`}>
                <p className={`font-medium ${
                  capacityInfo.status === 'OK' ? 'text-green-400' : 
                  capacityInfo.status === 'NO_STAFF_AVAILABLE' ? 'text-red-400' : 'text-amber-400'
                }`}>
                  {capacityInfo.status === 'OK' ? '✓ Capacity Available' : 
                   capacityInfo.status === 'NO_STAFF_AVAILABLE' ? '✗ No Staff Available' : '⚠ Full Capacity'}
                </p>
                {capacityInfo.recommendation && (
                  <p className="text-sm text-gray-300 mt-1">{capacityInfo.recommendation}</p>
                )}
              </div>
              
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-800 p-3 rounded-xl text-center">
                  <p className="text-2xl font-bold text-cyan-400">{capacityInfo.total_staff}</p>
                  <p className="text-xs text-gray-500">Staff</p>
                </div>
                <div className="bg-gray-800 p-3 rounded-xl text-center">
                  <p className="text-2xl font-bold text-green-400">{capacityInfo.total_available_capacity}</p>
                  <p className="text-xs text-gray-500">Available</p>
                </div>
                <div className="bg-gray-800 p-3 rounded-xl text-center">
                  <p className="text-2xl font-bold text-amber-400">{capacityInfo.queued_slips_count}</p>
                  <p className="text-xs text-gray-500">Queued</p>
                </div>
              </div>
              
              {/* Staff List */}
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-400">Staff Details (Max: {capacityInfo.max_pieces_per_staff} pcs each)</p>
                <p className="text-xs text-gray-500 mb-2">* Capacity blocked only after staff scans bundle for main label printing</p>
                {capacityInfo.staff?.map((s) => (
                  <div key={s.staff_id} className={`p-3 rounded-xl border ${
                    s.is_full ? 'bg-red-900/20 border-red-700' : 'bg-gray-800 border-gray-700'
                  }`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{s.staff_name || s.staff_id}</p>
                        <p className="text-xs text-gray-500">{s.staff_id}</p>
                        {s.pending_handovers > 0 && (
                          <p className="text-xs text-blue-400">{s.pending_handovers} pending handovers</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${s.is_full ? 'text-red-400' : 'text-green-400'}`}>
                          {s.available_capacity} free
                        </p>
                        <p className="text-xs text-gray-500">{s.current_pieces}/{s.max_pieces} scanned</p>
                      </div>
                    </div>
                    
                    {/* Show stuck scanned bundles if any */}
                    {s.stuck_count > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <p className="text-xs text-amber-400 mb-1">
                          ⚠ {s.stuck_count} stuck scanned bundle(s) ({">"}4h old)
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => clearStuckCapacity(s.staff_id)}
                          className="text-xs border-amber-600 text-amber-300"
                          disabled={loading}
                        >
                          Clear for {s.staff_name || s.staff_id}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Queued Slips */}
              {capacityInfo.queued_slips?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-400">Queued Slips</p>
                  {capacityInfo.queued_slips.map((q, idx) => (
                    <div key={idx} className="bg-gray-800 p-2 rounded-lg text-sm">
                      <span className="font-mono text-amber-400">{q.slip_no}</span>
                      <span className="text-gray-500 ml-2">({q.total_pieces} pcs)</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Actions */}
              <div className="space-y-2 pt-2">
                <Button
                  onClick={() => clearStuckCapacity()}
                  disabled={loading}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  Clear All Stuck Capacity
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCapacityModal(false)}
                  className="w-full border-gray-600"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Handover Queue Indicator - visible on ALL steps when queue has items */}
      {pendingQueueBundles.length > 0 && step !== 'select-slip' && !showQueueOverlay && (
        <button
          onClick={() => setShowQueueOverlay(true)}
          className="fixed bottom-20 left-4 right-4 z-40 bg-orange-600 text-white px-4 py-3 rounded-xl shadow-lg flex items-center justify-center gap-2"
          data-testid="floating-queue-indicator"
        >
          <AlertTriangle className="w-5 h-5" />
          <span className="font-semibold text-sm">Handover Queue • {pendingQueueTotal.bundles} bundles • {pendingQueueTotal.pieces} pcs</span>
        </button>
      )}

      {/* Handover Queue Overlay */}
      {showQueueOverlay && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
          <div className="bg-gray-900 flex-1 overflow-y-auto">
            <div className="bg-orange-700 px-4 py-4 flex items-center justify-between sticky top-0 z-10">
              <div>
                <h2 className="text-lg font-bold text-white">Handover Queue</h2>
                <p className="text-orange-200 text-xs">{pendingQueueTotal.bundles} bundles • {pendingQueueTotal.pieces} pcs waiting</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowQueueOverlay(false)}
                className="text-white hover:bg-white/20"
              >
                <XCircle className="w-6 h-6" />
              </Button>
            </div>
            <div className="p-4 space-y-3">
              {pendingQueueBundles.map((item, idx) => {
                const qId = item.queue_id || item.handover_id;
                return (
                  <div key={idx} className="bg-gray-800 border border-orange-700/50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-orange-300 text-sm">Slip: {item.slip_no}</span>
                      <span className="text-xs text-gray-400">
                        {item.total_bundles} bundle{item.total_bundles !== 1 ? 's' : ''} • {item.total_pieces} pcs
                      </span>
                    </div>
                    {item.design_name && <p className="text-sm text-gray-300 mb-1">{item.design_name}</p>}
                    {(item.picker_name || item.picker_staff_id) && (
                      <p className="text-xs text-gray-500 mb-2">Picker: {item.picker_name || item.picker_staff_id}</p>
                    )}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {item.bundles?.slice(0, 4).map((bundle, bIdx) => (
                        <span key={bIdx} className="text-xs text-orange-200/80 bg-orange-950/50 px-1.5 py-0.5 rounded">
                          {bundle.bundle_code}
                        </span>
                      ))}
                      {item.bundles?.length > 4 && (
                        <span className="text-xs text-orange-400">+{item.bundles.length - 4} more</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const response = await apiClient.post(`/wms/dispatch/handover-queue/retry-assignment?queue_id=${qId}`);
                          if (response.data.success) {
                            if (response.data.assigned) {
                              toast.success(`Assigned ${response.data.assigned_bundles || ''} bundles to staff`);
                              if (response.data.handover_id) {
                                setHandoverId(response.data.handover_id);
                                setHandoverSlipNo(item.slip_no);
                                setHandoverAssignments(response.data.assignments || []);
                                setIsQueued(false);
                                setShowQueueOverlay(false);
                                setStep('handover');
                              }
                            } else {
                              toast.warning('Still no capacity. Bundles remain queued.');
                            }
                            fetchPendingHandoverQueue();
                          }
                        } catch (error) {
                          toast.error('Failed to retry assignment');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Retry Assignment
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileSFGPicking;
